import { Component, OnInit, HostBinding, inject } from '@angular/core';
import { PageComponent, Transfer, sleep } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { ApiShow, ApiTopic, FollowApi } from '@tytapp/api';
import { TopicsService } from '@tytapp/topics';
import { isClientSide } from '@tytapp/environment-utils';
import { FollowsService } from '@tytapp/social/follows.service';
import { ShowsService } from '@tytapp/shows';

@Component({
    selector: 'app-shows-page',
    templateUrl: './shows-page.component.html',
    styleUrls: ['./shows-page.component.scss']
})
export class ShowsPageComponent extends PageComponent implements OnInit {
    private showsService = inject(ShowsService);
    private userService = inject(UserService);
    private topicsService = inject(TopicsService);
    private followApi = inject(FollowApi);
    private followService = inject(FollowsService);

    get pageComponentId() {
        return 'shows';
    }

    @Transfer()
    shows: ApiShow[];

    @Transfer()
    topics: ApiTopic[];

    @Transfer()
    followedShows: ApiShow[] = [];

    get sections() {
        return this.followedShows?.length ?
            [this.followedShows, this.shows] :
            [this.shows];
    }

    get user() {
        return this.userService.user;
    }

    async init() {
        this.pageTitle = 'Shows';
        this.head.setCanonicalLink(`/shows`);

        await this.userService.ready;

        let followedShows: ApiShow[] = [];
        let [shows, followings, topics] = await Promise.all([
            this.showsService.all().then(shows => shows.filter(x => !x.is_past_show)),
            this.userService.user ? this.followService.getFollowedShows() : Promise.resolve([]),
            this.topicsService.all()
        ]);

        if (this.userService.user) {
            try {
                followedShows = shows
                    .filter(x => followings.some(following => x.id === following.show_id))
                    .filter(x => x);
                shows = shows.filter(show => !followedShows.includes(show))
            } catch (e) {
                this.logger.error(`[Shows Page] Error while fetching current user's followed shows:`);
                this.logger.error(e);
            }
        }

        this.topics = topics;
        this.shows = shows;
        this.followedShows = followedShows;
    }

    hosts(show: ApiShow) {
        return show.hosts?.map(({ name }) => name).join(' & ') ?? '';
    }

    canWatch(show: ApiShow) {
        return !show.is_premium || this.user?.entitled;
    }

    setAttribution() {
        this.userService.source = 'page:shows';
    }

    urlForShow(show: ApiShow) {
        return this.user?.can_access_tytnation && show.username ?
            `/@${ show.username }` :
            `/shows/${ show.slug || show.id }`;
    }

    get isSmallScreen() {
        let smallScreen = 576;
        return isClientSide() && window.innerWidth <= smallScreen;
    }
}
